import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { PlayerService } from './player.service';
import { PlayerState, PlayerStatus } from './state/player.reducer';
import {
  AutoplayPermissionDenied,
  AutoplayPermissionAllowed,
  Init,
  InitListeners,
  Mute,
  RefreshLocation,
  RefreshLocationInformation,
  ResetAll,
  ToggleMute,
  SendWrongRuntime,
  PlayNext,
  LoadNextTrackAndPlay,
  Unmute,
  RefreshBrowser,
  PlayerActionTypes,
} from './state/player.actions';
import * as interruptionActions from './state/interruptions.actions';
import {
  isActiveTrackSilence,
  isAdActive,
  isControlVisible,
  isLocationLocked,
  isMuted,
  isPlayerRefreshing,
  isPrayerTimerActive,
  selectCurrentSongArtistName,
  selectCurrentSongName,
  selectLocation,
  selectLocationGuid,
  selectLocationPlaylistName,
  selectPlayerStatus,
  selectPlayerStatusMessage,
  selectPlayerVolume,
  selectActiveTrack,
  selectCurrentPlaylist
} from './state/player.selectors';
import { selectAreInterruptionsReady, selectLocalTimeOffset, selectNextPrayerTimeInterruptionDate } from './state/interruptions.selectors';
import { first, take, tap } from 'rxjs/operators';
import { LayoutService } from '../layout/layout.service';
import { AdInterruption } from './models/AdInterruption';
import { PlayAd, StopAd } from './state/interruptions.actions';
import { BehaviorSubject, timer } from 'rxjs';
import { TypeMessage } from '../layout/state/layout.actions';
import { MainAudioService } from './audio-sources/main-audio.service';
import { Capacitor } from '@capacitor/core';
import { AudioService } from './audio-sources/audio.service';

@Injectable({
  providedIn: 'root',
})
export class PlayerFacade {
  isMuted$ = this.store.pipe(select(isMuted));
  isLocked$ = this.store.pipe(select(isLocationLocked));
  isPlayerRefreshing$ = this.store.pipe(select(isPlayerRefreshing));
  areInterruptionsReady$ = this.store.pipe(select(selectAreInterruptionsReady));
  isPrayerTimeActive$ = this.store.pipe(select(isPrayerTimerActive));
  isAdActive$ = this.store.pipe(select(isAdActive));
  isSilenceActive$ = this.store.pipe(select(isActiveTrackSilence));

  locationGuid$ = this.store.pipe(select(selectLocationGuid));
  nextPrayerTimeInterruptionDate$ = this.store.pipe(select(selectNextPrayerTimeInterruptionDate));
  playlistName$ = this.store.pipe(select(selectLocationPlaylistName));
  currentSongName$ = this.store.pipe(select(selectCurrentSongName));
  currentSongArtistName$ = this.store.pipe(select(selectCurrentSongArtistName));
  getLocalTimeOffset$ = this.store.pipe(select(selectLocalTimeOffset));

  playerStatus$ = this.store.pipe(select(selectPlayerStatus));
  playerStatusMessage$ = this.store.pipe(select(selectPlayerStatusMessage));
  playerVolume$ = this.store.pipe(select(selectPlayerVolume));

  selectActiveTrack$ = this.store.pipe(select(selectActiveTrack));

  location$ = this.store.pipe(select(selectLocation));

  isControlVisible$ = this.store.pipe(select(isControlVisible));

  currentPlaylist$ = this.store.pipe(select(selectCurrentPlaylist));

  playerStatus = PlayerStatus;

  setToReloadActive$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private store: Store<PlayerState>,
    private playerService: PlayerService,
    private layoutService: LayoutService,
    // private audioService: AudioService
  ) {}

  init(locationGuid: string) {
    this.store.dispatch(Init({ locationGuid }));
  }

  refreshLocation() {
    this.store.dispatch(RefreshLocation());
  }

  toggleMute() {
    this.store.dispatch(ToggleMute());
  }

  hardRefreshBrowser(error: string, noTrackCache?: boolean) {
    const storeValue = localStorage.getItem('trackHardRefresh');
    let countHardRefresh = Number(storeValue ? storeValue : '0');
    if (countHardRefresh < 4 || noTrackCache) {
      if (!noTrackCache) {
        countHardRefresh = countHardRefresh + 1;
        localStorage.setItem('trackHardRefresh', JSON.stringify(countHardRefresh));
      }
      this.layoutService.showMessage(TypeMessage.info, 'The player encoutered some errors and will auto refresh', 5000);
      setTimeout(() => {
        this.refreshBrowser();
      }, 5000);
    } else {
      this.layoutService.showMessage(TypeMessage.info, 'The player encoutered some errors, please contact support', 20000);
    }
  }

  mute() {
    this.store.dispatch(Mute());
  }

  unmute() {
    this.store.dispatch(Unmute());
  }

  initListeners() {
    this.store.dispatch(InitListeners());
  }

  resetAll() {
    this.store.dispatch(ResetAll());
  }

  locationInformationRefresh() {
    this.store.dispatch(RefreshLocationInformation());
  }

  autoplayPermissionDenied() {
    this.store.dispatch(AutoplayPermissionDenied());
  }

  autoplayPermissionAllowed() {
    this.store.dispatch(AutoplayPermissionAllowed());
  }

  submitWrongRuntime(track, runtime) {
    this.store.dispatch(SendWrongRuntime({ track, runtime }));
  }

  playBirthdayTrackNow(bdayAsAdInterruption: AdInterruption) {
    this.store
      .pipe(
        select(isMuted),
        take(1),
        tap(() => {
          this.triggerStopAd(bdayAsAdInterruption.runTime * 1000);
        }),
      )
      .subscribe(muted => {
        this.store.dispatch(PlayAd({ adInterruption: bdayAsAdInterruption, previousMutedState: muted }));
      });
  }

  triggerStopAd(endAfter: number) {
    const stopTime = Capacitor.getPlatform() === 'ios' ? 2000 : 0;
    setTimeout(() => {
      setTimeout(()=>{
        this.store.dispatch(StopAd());
      },stopTime);
    }, endAfter);
  }

  playNext() {
    this.store.dispatch(PlayNext());
  }

  refreshBrowser(immediate = false) {
    this.store.dispatch(RefreshBrowser({immediate}));
  }

  showMessage(message: string, type: TypeMessage, timeout = 3000) {
    this.layoutService.showMessage(type, message, timeout);
  }

  // stopAllPlayerAudio() {
  //   this.audioService.stopPromise();
  // }

  // triggerMainPlayer() {
  //   this.mainAudioService.playPromise(1);
  // }
}
